// ColorPicker — the in-house swatch editor. The native <input type="color"> on Android is
// an eight-square preset dialog with "Custom" buried a tap deeper — useless for palette
// authoring (owner report, dev gate). This popover speaks the editor's slider idiom
// instead: a live preview, a hex field, and Hue/Saturation/Lightness sliders whose tracks
// render the actual ramp for the current colour, plus the platform EyeDropper where one
// exists (Chromium desktop). Changes apply LIVE — the orbs/PiP track every drag, matching
// every other editor control — and closing keeps the last value (no cancel ceremony).
//
// HSL is the working state while open (seeded from the incoming hex): editing through hex
// alone would collapse hue/saturation whenever lightness hits 0/100 (black/white erase
// them), which makes slider exploration maddening.

import { useState } from "react";
import { Modal } from "./Modal";

export interface ColorPickerProps {
  /** The swatch being edited, as #rrggbb. */
  value: string;
  /** Dialog label, e.g. "Colour 3". */
  label: string;
  /** Fired on EVERY change with a #rrggbb hex (live-apply). */
  onChange: (hex: string) => void;
  onClose: () => void;
}

/** #rrggbb → [hue 0-360, saturation 0-100, lightness 0-100]. */
export function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, Math.round(l * 100)]; // achromatic
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [Math.round(h * 60), Math.round(s * 100), Math.round(l * 100)];
}

/** [hue 0-360, saturation 0-100, lightness 0-100] → #rrggbb (lowercase). */
export function hslToHex(h: number, s: number, l: number): string {
  const sn = s / 100;
  const ln = l / 100;
  const a = sn * Math.min(ln, 1 - ln);
  const f = (n: number): string => {
    const k = (n + h / 30) % 12;
    const c = ln - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)));
    return Math.round(c * 255)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

const HEX_RE = /^#?([0-9a-f]{6})$/i;

/** The platform eyedropper, where the browser ships one (Chromium desktop). */
interface EyeDropperLike {
  open(): Promise<{ sRGBHex: string }>;
}
function eyeDropperCtor(): (new () => EyeDropperLike) | null {
  const w = window as { EyeDropper?: new () => EyeDropperLike };
  return w.EyeDropper ?? null;
}

export function ColorPicker({ value, label, onChange, onClose }: ColorPickerProps): React.JSX.Element {
  const [[h, s, l], setHsl] = useState<[number, number, number]>(() => hexToHsl(value));
  // The hex field is a free-typing draft (partial input must not thrash the sliders);
  // slider edits rewrite it, a VALID typed hex applies + reseeds the sliders.
  const [hexDraft, setHexDraft] = useState(() => hslToHex(...hexToHsl(value)));

  function applyHsl(nh: number, ns: number, nl: number): void {
    setHsl([nh, ns, nl]);
    const hex = hslToHex(nh, ns, nl);
    setHexDraft(hex);
    onChange(hex);
  }

  function applyHexDraft(text: string): void {
    setHexDraft(text);
    const m = HEX_RE.exec(text.trim());
    if (!m) return; // keep typing — applied once it parses
    const hex = `#${m[1]!.toLowerCase()}`;
    setHsl(hexToHsl(hex));
    onChange(hex);
  }

  async function pickFromScreen(): Promise<void> {
    const Ctor = eyeDropperCtor();
    if (!Ctor) return;
    try {
      const { sRGBHex } = await new Ctor().open();
      applyHexDraft(sRGBHex);
    } catch {
      /* user dismissed the eyedropper — nothing to do */
    }
  }

  const current = hslToHex(h, s, l);

  return (
    <Modal label={`Edit ${label}`} onClose={onClose} testId="color-picker" className="colorpicker-overlay">
      <div className="colorpicker">
        <div className="colorpicker__head">
          <span className="colorpicker__chip" style={{ background: current }} aria-hidden="true" />
          <label className="colorpicker__hexlabel">
            Hex
            <input
              className="colorpicker__hex"
              value={hexDraft}
              maxLength={7}
              spellCheck={false}
              autoCapitalize="off"
              aria-label={`${label} hex value`}
              data-testid="picker-hex"
              onChange={(e) => applyHexDraft(e.target.value)}
            />
          </label>
          {eyeDropperCtor() && (
            <button
              type="button"
              className="btn colorpicker__dropper"
              onClick={() => void pickFromScreen()}
              data-testid="picker-eyedropper"
            >
              💉 Pick from screen
            </button>
          )}
        </div>

        <div className="colorpicker__slider">
          <span className="colorpicker__slider-label">hue</span>
          <input
            type="range"
            min={0}
            max={360}
            step={1}
            value={h}
            aria-label={`${label} hue`}
            data-testid="picker-hue"
            className="colorpicker__range colorpicker__range--hue"
            onChange={(e) => applyHsl(Number(e.target.value), s, l)}
          />
          <span className="colorpicker__slider-num">{h}°</span>
        </div>
        <div className="colorpicker__slider">
          <span className="colorpicker__slider-label">sat</span>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={s}
            aria-label={`${label} saturation`}
            data-testid="picker-sat"
            className="colorpicker__range"
            style={{
              background: `linear-gradient(90deg, ${hslToHex(h, 0, l)}, ${hslToHex(h, 100, l)})`,
            }}
            onChange={(e) => applyHsl(h, Number(e.target.value), l)}
          />
          <span className="colorpicker__slider-num">{s}%</span>
        </div>
        <div className="colorpicker__slider">
          <span className="colorpicker__slider-label">light</span>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={l}
            aria-label={`${label} lightness`}
            data-testid="picker-light"
            className="colorpicker__range"
            style={{
              background: `linear-gradient(90deg, #000000, ${hslToHex(h, s, 50)}, #ffffff)`,
            }}
            onChange={(e) => applyHsl(h, s, Number(e.target.value))}
          />
          <span className="colorpicker__slider-num">{l}%</span>
        </div>

        <button type="button" className="btn btn--primary colorpicker__done" onClick={onClose} data-testid="picker-done">
          Done
        </button>
      </div>
    </Modal>
  );
}
